import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    FunctionField,
    BooleanField,
    DateField,
    EditButton,
    ShowButton,
    Filter,
    TextInput,
    SelectInput,
    BooleanInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    usePermissions,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Chip,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Lock,
    ContentCopy,
    Equalizer,
    Star,
    Schedule,
    ElectricBolt,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LMSTemplatesFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.lms-templates.filters.search')}
                source="search"
                alwaysOn
            />
            <SelectInput
                source="type"
                label={translate('resources.lms-templates.fields.type')}
                choices={[
                    { id: 'equal', name: translate('resources.lms-templates.types.equal') },
                ]}
            />
            <BooleanInput
                label={translate('resources.lms-templates.filters.activeOnly')}
                source="isActive"
            />
            <BooleanInput
                label={translate('resources.lms-templates.filters.systemOnly')}
                source="isSystem"
            />
        </Filter>
    );
};

const ListActions = () => {
    const { permissions } = usePermissions();
    const canCreate = permissions?.includes('lms-templates:write:all');

    return (
        <TopToolbar>
            {canCreate && <CreateButton />}
            <ExportButton />
            <RefreshButton />
        </TopToolbar>
    );
};

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const TypeIcon = ({ type }: { type: string }) => {
    switch (type) {
        case 'equal':
            return <Equalizer sx={{ fontSize: 16 }} />;
        case 'priority':
            return <Star sx={{ fontSize: 16 }} />;
        case 'scheduled':
            return <Schedule sx={{ fontSize: 16 }} />;
        case 'dynamic':
            return <ElectricBolt sx={{ fontSize: 16 }} />;
        default:
            return <Equalizer sx={{ fontSize: 16 }} />;
    }
};

const MobileTemplateList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const navigate = useNavigate();

    if (isLoading || !data) return null;

    const typeColors: Record<string, { bg: string; color: string }> = {
        equal: { bg: '#E8F5E8', color: '#00C853' },
        priority: { bg: '#E3F2FD', color: '#1976D2' },
        scheduled: { bg: '#FFF3E0', color: '#EF6C00' },
        dynamic: { bg: '#F3E5F5', color: '#7B1FA2' },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {data.map((record: any) => {
                const colors = typeColors[record.type] || typeColors.equal;
                const usageCount = record.usageCount || 0;

                return (
                    <RecordContextProvider key={record.id} value={record}>
                        <Box
                            onClick={() => navigate(`/lms-templates/${record.id}/show`)}
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: 2,
                                p: 2,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                cursor: 'pointer',
                                '&:active': { backgroundColor: '#F5F5F5' },
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                    {Boolean(record.isSystem) && (
                                        <Lock sx={{ fontSize: 16, color: '#666', flexShrink: 0 }} />
                                    )}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {record.name || ''}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={record.isActive ? translate('status.active') : translate('status.inactive')}
                                    size="small"
                                    sx={{
                                        backgroundColor: record.isActive ? '#E8F5E8' : '#FFEBEE',
                                        color: record.isActive ? '#009A75' : '#F44336',
                                        fontWeight: 500,
                                        fontSize: '0.7rem',
                                        height: 24,
                                        flexShrink: 0,
                                        ml: 1,
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        icon={<TypeIcon type={record.type} />}
                                        label={translate(`resources.lms-templates.types.${record.type}`)}
                                        size="small"
                                        component="div"
                                        clickable={false}
                                        sx={{
                                            backgroundColor: colors.bg,
                                            color: colors.color,
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            height: 24,
                                            '& .MuiChip-icon': { color: colors.color },
                                            pointerEvents: 'none',
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {usageCount} {usageCount === 1 ? translate('resources.lms-templates.loadGroup') : translate('resources.lms-templates.loadGroupsPlural')}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {record.createdAt ? new Date(record.createdAt).toLocaleDateString(undefined, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    }) : ''}
                                </Typography>
                            </Box>
                        </Box>
                    </RecordContextProvider>
                );
            })}
        </Box>
    );
};

export const LMSTemplatesList = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const canEdit = permissions?.includes('lms-templates:write:all');
    const canCreate = permissions?.includes('lms-templates:write:all');
    const canDelete = permissions?.includes('lms-templates:admin:all');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<LMSTemplatesFilter />}
                actions={isMobile ? <MobileListActions /> : <ListActions />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'name', order: 'ASC' }}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileTemplateList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 600,
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <FunctionField
                            source="name"
                            label={translate('resources.lms-templates.fields.name')}
                            render={(record: any) => {
                                if (!record) return null;
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {Boolean(record.isSystem) && (
                                            <Tooltip title={translate('resources.lms-templates.systemTemplate')}>
                                                <Lock sx={{ fontSize: 16, color: '#666' }} />
                                            </Tooltip>
                                        )}
                                        <Typography variant="body2" sx={{ fontWeight: Boolean(record.isSystem) ? 600 : 400 }}>
                                            {record.name || ''}
                                        </Typography>
                                    </Box>
                                );
                            }}
                        />

                        <FunctionField
                            source="type"
                            label={translate('resources.lms-templates.fields.type')}
                            render={(record: any) => {
                                const typeColors: Record<string, { bg: string; color: string }> = {
                                    equal: { bg: '#E8F5E8', color: '#00C853' },
                                    priority: { bg: '#E3F2FD', color: '#1976D2' },
                                    scheduled: { bg: '#FFF3E0', color: '#EF6C00' },
                                    dynamic: { bg: '#F3E5F5', color: '#7B1FA2' },
                                };

                                const colors = typeColors[record.type] || typeColors.equal;

                                return (
                                    <span style={{ pointerEvents: 'none' }}>
                                        <Chip
                                            icon={<TypeIcon type={record.type} />}
                                            label={translate(`resources.lms-templates.types.${record.type}`)}
                                            size="small"
                                            component="div"
                                            clickable={false}
                                            sx={{
                                                backgroundColor: colors.bg,
                                                color: colors.color,
                                                fontWeight: 500,
                                                '& .MuiChip-icon': {
                                                    color: colors.color,
                                                },
                                            }}
                                        />
                                    </span>
                                );
                            }}
                        />

                        <TextField
                            source="description"
                            label={translate('resources.lms-templates.fields.description')}
                            sx={{
                                '& .RaTextField-root': {
                                    maxWidth: '300px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        />

                        <FunctionField
                            source="usageCount"
                            label={translate('resources.lms-templates.fields.usage')}
                            render={(record: any) => {
                                const count = record.usageCount || 0;
                                return (
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {count}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {count === 1 ? translate('resources.lms-templates.loadGroup') : translate('resources.lms-templates.loadGroupsPlural')}
                                        </Typography>
                                    </Box>
                                );
                            }}
                        />

                        <BooleanField
                            source="isActive"
                            label={translate('resources.lms-templates.fields.status')}
                            valueLabelTrue={translate('status.active')}
                            valueLabelFalse={translate('status.inactive')}
                            TrueIcon={CheckCircle}
                            FalseIcon={Cancel}
                            sx={{
                                '& .RaBooleanField-true': {
                                    color: '#009A75',
                                },
                                '& .RaBooleanField-false': {
                                    color: '#F44336',
                                },
                            }}
                        />

                        <DateField
                            source="createdAt"
                            label={translate('resources.lms-templates.fields.createdAt')}
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            }}
                        />

                        <FunctionField
                            label={translate('resources.lms-templates.fields.actions')}
                            render={(record: any) => (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <ShowButton />
                                    {canEdit && !record.isSystem && <EditButton />}
                                    {canEdit && (
                                        <Tooltip title={translate('resources.lms-templates.actions.clone')}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log('Clone template:', record.id);
                                                }}
                                                sx={{
                                                    color: 'rgba(0, 0, 0, 0.54)',
                                                    '&:hover': {
                                                        color: 'rgba(0, 0, 0, 0.87)',
                                                    },
                                                }}
                                            >
                                                <ContentCopy sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )}
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && canCreate && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/lms-templates/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
